import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ExcursionsSection } from "@/components/ExcursionsSection";
import { SeoHead } from "@/components/SeoHead";
import { LocalBusinessStructuredData } from "@/components/StructuredData";
import { AdminToggle } from "@/components/AdminToggle";
import { EditableImage } from "@/components/EditableImage";
import { useSiteImages } from "@/hooks/useSiteImages";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { SafeImage } from "@/components/SafeImage";
import { ChevronDown } from "lucide-react";

const Excursions = () => {
  const { images: siteImages, refetch: refetchImages } = useSiteImages();
  const { isAdmin } = useAdmin();

  const heroUrl = siteImages['hero-excursions']?.image_url || '/hero-excursions.jpg';

  const handleHeroUpdate = async (newUrl: string) => {
    await supabase
      .from('site_images')
      .upsert({ image_key: 'hero-excursions', image_url: newUrl, category: 'excursions', is_active: true, alt_text_fr: 'Excursions à Rio de Janeiro - Roca Rio' }, { onConflict: 'image_key' });
    await refetchImages();
  };

  const handleHeroDelete = async () => {
    await supabase.from('site_images').delete().eq('image_key', 'hero-excursions');
    await refetchImages();
  };

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Excursions à Rio - Roca Rio | Guide Francophone Certifiée"
        description="Explorez Rio de Janeiro avec nos excursions authentiques : randonnées (Pedra da Gávea, Dois Irmãos), visites culturelles, tourisme solidaire. Guide francophone certifiée."
        canonical="https://rocario-cleacarioca.com/excursions"
        keywords="excursions Rio de Janeiro, randonnée Pedra da Gávea, Dois Irmãos, Corcovado, tourisme solidaire Rio, guide francophone Rio, visite favelas"
      />
      
      <LocalBusinessStructuredData type="TravelAgency" />
      
      <Header />
      <main className="bg-background">
        {/* Hero avec image de fond */}
        <section className="relative h-[60vh] sm:h-[70vh] flex items-end overflow-hidden pb-16 sm:pb-20">
          {/* Image de fond */}
          <div className="absolute inset-0">
            {isAdmin ? (
              <EditableImage
                src={heroUrl || '/placeholder.svg'}
                alt="Excursions à Rio de Janeiro"
                className="w-full h-full object-cover object-center"
                bucket="site-assets"
                path="pages/hero-excursions.jpg"
                loading="eager"
                aspectRatio={16 / 9}
                onImageUpdate={handleHeroUpdate}
                onImageDelete={handleHeroDelete}
              />
            ) : heroUrl ? (
              <SafeImage
                src={heroUrl}
                alt="Excursions à Rio de Janeiro"
                className="w-full h-full object-cover object-center"
                loading="eager"
              />
            ) : (
              <div className="w-full h-full bg-roca-blue-dark" />
            )}
          </div>

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />

          {/* Contenu */}
          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="w-12 h-1 bg-roca-yellow mb-6" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-bold text-white mb-4 drop-shadow-2xl">
                Explorez Rio avec Cléa
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl drop-shadow-lg">
                Excursions authentiques et solidaires par une guide française installée à Rio
              </p>
              <div 
                className="inline-flex items-center gap-3 text-white/80 hover:text-white transition-colors cursor-pointer group"
                onClick={() => {
                  const element = document.getElementById('excursions-list');
                  element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <span className="text-base font-medium tracking-wide">Découvrir les excursions</span>
                <div className="w-12 h-px bg-white/50 group-hover:w-16 group-hover:bg-roca-yellow transition-all duration-300" />
              </div>
            </div>
          </div>

          {/* Chevron scroll */}
          <div 
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 animate-bounce cursor-pointer"
            onClick={() => document.getElementById('excursions-list')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <ChevronDown className="w-6 h-6 text-white/70" />
          </div>
        </section>
        
        <div id="excursions-list">
          <ExcursionsSection isFullPage />
        </div>
      </main>
      <Footer />
      
      <AdminToggle />
    </div>
  );
};
export default Excursions;
